import React, { useMemo, useRef, Fragment } from "react";
import { Line, XAxis, YAxis, Area, CartesianGrid, Tooltip } from "recharts";
import { format } from "date-fns";
import {
  Box,
  Typography,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { cloneDeep } from "lodash";
import { toCustomShort } from "@helpers";
import { Chart } from "../BaseChartTools";
import { LoadingPlaceHolder } from "../../common/progress/LoadingPlaceHolder";
import { PredictionChartData, PredictionChartItem } from "./types";
import { calculateYAxisDomain } from "./helpers";

const ItemDot = styled(Box)<{ color: string }>(({ color }) => ({
  backgroundColor: color,
  borderRadius: "50%",
  width: "8px",
  height: "8px",
}));

const StyledTooltip = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.tooltipPrimary};
  color: ${({ theme }) => theme.palette.text.onTooltipPrimary};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: ${({ theme }) => theme.shadows[3]};
`;

const ItemArea = ({ id, color }: { id: string; color: string }) => {
  return (
    <>
      <defs key={`defs-${id}`}>
        <linearGradient id={`colorUv-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="40%" stopColor={color} stopOpacity={0.12} />
          <stop offset="100%" stopColor={color} stopOpacity={0.012} />
        </linearGradient>
      </defs>
      <Area
        isAnimationActive={false}
        key={`area-${id}`}
        type="monotone"
        stroke="transparent"
        fill={`url(#colorUv-${id})`}
        dataKey={`${id}.value`}
        activeDot={false}
      />
    </>
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Partial<{
    value: number;
    dataKey: string;
  }>[];
  label?: string;
  items: PredictionChartItem[];
}

const CustomTooltip = ({
  active,
  payload,
  label,
  items,
}: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <StyledTooltip>
        {items.map((item) => {
          const data = payload.find((p) => {
            const id = p.dataKey?.split(".")[0];
            const type = p.dataKey?.split(".")[1];

            return id === item.id && type === "value";
          });
          return (
            <Box display="flex" gap={2} alignItems="center" key={item.id}>
              <ItemDot color={item.color} />
              <Typography variant="body3">{item.name}:</Typography>
              <Typography variant="body4" color="text.tertiary">
                {toCustomShort(data?.value || 0)} %
              </Typography>
            </Box>
          );
        })}
      </StyledTooltip>
    );
  }

  return null;
};

const dateFormatter = (v: Date, index: number) => {
  return index === 0 ? "Heute" : format(v, "yyyy");
};

export type PredictionChartProps = {
  data: PredictionChartData[];
  items: PredictionChartItem[];
  selectedItemId?: string;
  displayMinMax?: boolean;
  displayLegend?: boolean;
  tickFormatter?: (v: Date, index: number) => string;
  isFetching?: boolean;
  tickCount?: number;
};

export const PredictionChart = ({
  data,
  items,
  selectedItemId,
  displayMinMax = false,
  tickFormatter = dateFormatter,
  displayLegend = false,
  isFetching = false,
  tickCount,
}: PredictionChartProps) => {
  const theme = useTheme();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));
  const ref = useRef<HTMLDivElement>(null);
  const isAnimationDisabled = !!selectedItemId;

  const yAxisDomain = useMemo(
    () => calculateYAxisDomain(data, items),
    [data, items]
  );

  const orderedItems = useMemo(() => {
    const orderedItems = [...items];
    orderedItems.sort((a, b) => (a.id === selectedItemId ? 1 : -1));
    return orderedItems;
  }, [items, selectedItemId]);

  const filledInData = useMemo(() => {
    const newData = cloneDeep(data);
    newData.forEach((dataItem, index) => {
      items.forEach((item) => {
        if (!dataItem[item.id]) {
          dataItem[item.id] = newData[index - 1]?.[item.id];
        }
      });
    });

    return newData;
  }, [data, items]);

  const tooltipItems = items.filter((item) => !item.hidden);

  if (!data.length) return null;

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
    >
      {displayLegend && (
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          mb={3}
          flexWrap={isLessSm ? "wrap" : "nowrap"}
        >
          {items.map((item) => (
            <Box display="flex" alignItems="center" key={item.id} gap={2}>
              <ItemDot color={item.color} />
              <Typography variant="body3">{item.name}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box zIndex={1} width="100%" height="100%">
        {isFetching ? (
          <LoadingPlaceHolder height="100%" width="100%" />
        ) : (
          <Chart
            data={filledInData}
            ref={ref}
            margin={{
              top: 10,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <XAxis
              dataKey="date"
              padding={{ left: 10, right: 7 }}
              tickFormatter={tickFormatter}
              axisLine={false}
              tickLine={false}
              dy={30}
              interval="preserveStartEnd"
              minTickGap={5}
              tickCount={tickCount}
              tick={{ fill: theme.palette.grey[500], fontSize: "12px" }}
            />
            <YAxis
              orientation="right"
              axisLine={false}
              tickLine={false}
              tickCount={5}
              domain={yAxisDomain}
              tick={{ fill: theme.palette.grey[500], fontSize: "12px" }}
              tickFormatter={(v) => `${toCustomShort(v)} %`}
            />
            <Tooltip content={<CustomTooltip items={tooltipItems} />} />
            <CartesianGrid
              key="grid"
              vertical={false}
              stroke={theme.palette.border.secondary}
              strokeOpacity={1}
              strokeWidth={1}
            />
            {displayMinMax ? (
              <Area
                dataKey={`${selectedItemId}.minMax`}
                fill="rgba(201, 208, 218, 0.24)"
                stroke="transparent"
              />
            ) : null}
            {orderedItems.map((item) => (
              <Fragment key={item.id}>
                {!item.hidden && ItemArea({ id: item.id, color: item.color })}
                <Line
                  isAnimationActive={!isAnimationDisabled}
                  key={`line-${item.id}`}
                  type="monotone"
                  strokeWidth={item.id === selectedItemId ? 4 : 2}
                  stroke={item.color}
                  dataKey={`${item.id}.value`}
                  dot={false}
                  hide={item.hidden}
                />
              </Fragment>
            ))}
          </Chart>
        )}
      </Box>
    </Box>
  );
};
